import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import BoxedTitle from "@/components/BoxedTitle";
import { readCache, writeCache, clearCache } from "@/pages/admin/adminCache";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "vbb_admin_key";

/**
 * Market section landing page. Hosts the Competitors tracker entry
 * point and the pricing-memo download. When the market area gets its
 * own access key later, this is the natural place to add a separate
 * unlock check.
 *
 * Counter cache (adminCache) gives instant render on repeat visits
 * while a fresh fetch runs in the background.
 */
export default function Market() {
  const navigate = useNavigate();
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");
  const cached = readCache();
  const [highCount, setHighCount] = useState(cached.market?.high ?? null);
  const [total, setTotal] = useState(cached.market?.total ?? null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!key) {
      navigate("/login?next=/admin/market", { replace: true });
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/admin/market/competitors`, {
        headers: { "X-Admin-Key": key },
      })
      .then((res) => {
        if (cancelled) return;
        if (!sessionStorage.getItem(KEY_STORAGE)) return;
        const data = res.data || [];
        const market = {
          total: data.length,
          high: data.filter((c) => c.threat === "high").length,
        };
        setTotal(market.total);
        setHighCount(market.high);
        writeCache({ market });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          sessionStorage.removeItem(KEY_STORAGE);
          clearCache();
          setKey("");
          navigate("/login?next=/admin/market", { replace: true });
        } else {
          setError("Could not load market summary.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [key, navigate]);

  const onDownloadMemo = async () => {
    try {
      const resp = await fetch(`${API}/admin/market/pricing-memo`, {
        headers: { "X-Admin-Key": key },
      });
      if (!resp.ok) {
        alert(
          resp.status === 401
            ? "Session expired. Please sign in again."
            : `Download failed (${resp.status}).`
        );
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "VBB_Pricing_Memo.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Download failed. Check your connection.");
    }
  };

  const onLock = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    clearCache();
    setKey("");
    navigate("/login?next=/admin/market", { replace: true });
  };

  if (!key) return null;

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="admin-market-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="vbb-overline">
            <Link to="/admin" className="vbb-link" data-testid="crumb-admin">
              Admin
            </Link>{" "}
            / Market
          </div>
          <h1
            className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
            data-testid="admin-market-title"
          >
            <BoxedTitle size="lg" thick>
              Market.
            </BoxedTitle>
          </h1>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-[#6b6b6b]">
            Internal market intelligence — competitor tracking and pricing
            framing for advisors. This section will get its own access key
            in a future iteration.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-back-to-home"
          >
            ← Admin
          </Link>
          <button
            onClick={onLock}
            className="vbb-btn"
            data-testid="admin-lock-btn"
          >
            Lock
          </button>
        </div>
      </div>

      {error && (
        <div
          className="mt-8 p-4 border border-[#e34234] text-[#e34234] font-mono-tiny"
          data-testid="admin-error-banner"
        >
          {error}
        </div>
      )}

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Competitors card */}
        <Link
          to="/admin/market/competitors"
          className="block border border-[#0a0a0a] p-8 bg-white hover:bg-[#fafafa] transition-colors"
          data-testid="market-competitors-card"
        >
          <div className="vbb-overline">Competitor tracker</div>
          <h2 className="font-display uppercase text-3xl mt-4 tracking-tight">
            Competition.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[#2a2a2a]">
            13 boutique studios across Yamhill, Lincoln, Polk, Benton, and
            Linn counties. Editable notes per competitor, threat-level
            filters, and full teardowns for Buildable + Abide Web Design.
          </p>
          <div className="mt-6 flex items-center justify-between">
            <div className="font-mono-tiny text-[#6b6b6b]">
              {total === null ? (
                "Loading…"
              ) : (
                <>
                  {total} tracked ·{" "}
                  <span
                    className={
                      highCount > 0 ? "text-[#e34234]" : "text-[#6b6b6b]"
                    }
                    data-testid="market-high-threat-count"
                  >
                    {highCount} high threat
                  </span>
                </>
              )}
            </div>
            <span className="font-mono-tiny uppercase tracking-wider">
              Open →
            </span>
          </div>
        </Link>

        {/* Pricing memo card */}
        <button
          onClick={onDownloadMemo}
          className="block text-left border border-[#0a0a0a] p-8 bg-white hover:bg-[#fafafa] transition-colors"
          data-testid="market-pricing-memo-card"
        >
          <div className="vbb-overline">Advisor memo</div>
          <h2 className="font-display uppercase text-3xl mt-4 tracking-tight">
            Pricing memo.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[#2a2a2a]">
            Boutique web pricing tiers, where verybigbox.com lands,
            maintenance retainers, full local competitive landscape, and
            VBB positioning recommendations. PDF, regenerated from{" "}
            <span className="font-mono-tiny">scripts/gen_pricing_pdf.py</span>.
          </p>
          <div className="mt-6 flex items-center justify-between">
            <div className="font-mono-tiny text-[#6b6b6b]">
              Internal · Not for clients
            </div>
            <span className="font-mono-tiny uppercase tracking-wider text-[#e34234]">
              Download ↓
            </span>
          </div>
        </button>
      </div>
    </main>
  );
}
